/**
 * Navbar.jsx — Sticky top navigation bar for the M16S app.
 *
 * Behaviour:
 *   - Authenticated users see: Start, Dashboard, (Admin if admin), Logout.
 *   - Unauthenticated users see: Login, Sign Up.
 *   - The logo always links to "/". When the user is logged in, the click is
 *     intercepted and navigates to /dashboard instead (prevents landing-page revisit).
 *   - The ThemeToggle is shown to all users.
 *
 * Exports: Navbar
 */
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { ThemeToggle } from '../ThemeToggle';
import { Dna, ChevronDown } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

/**
 * Navbar — Top navigation bar. Renders different nav items based on auth state.
 */
export function Navbar() {
  const navigate = useNavigate();
  // Pull auth state to conditionally render nav items.
  const { isAuthenticated, logout, isAdmin } = useAuth();
  const [showPipelineMenu, setShowPipelineMenu] = useState(false);

  /**
   * Logs the user out and sends them to the login page.
   */
  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  /**
   * When logged in, intercepts the logo link and navigates to /dashboard instead
   * of the public landing page.
   * @param {React.MouseEvent} e
   */
  const handleLogoClick = (e) => {
    if (isAuthenticated) {
      e.preventDefault();
      // Navigate to dashboard instead of home when logged in
      navigate('/dashboard');
    }
  };

  return (
    <nav className="border-b bg-card/50 backdrop-blur-sm sticky top-0 z-50">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo — links to "/" but is intercepted when logged in. */}
          <Link to="/" onClick={handleLogoClick} className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
              <Dna className="h-4 w-4 text-primary-foreground" />
            </div>
            <span className="font-semibold text-lg hidden sm:inline">
              Microbial Analysis
            </span>
          </Link>

          {/* Nav Items */}
          <div className="flex items-center gap-4">
            {isAuthenticated ? (
              <>
                {/* Authenticated nav: Start (with dropdown menu), Dashboard, (Admin), Logout */}
                <div className="relative">
                  <Button
                    variant="ghost"
                    onClick={() => setShowPipelineMenu(!showPipelineMenu)}
                    className="flex items-center gap-1.5"
                  >
                    Start
                    <ChevronDown className="h-4 w-4" />
                  </Button>

                  {showPipelineMenu && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setShowPipelineMenu(false)} />
                      <div className="absolute right-0 mt-2 w-80 bg-card border border-border rounded-lg shadow-lg z-20 overflow-hidden">
                        <button
                          className="w-full text-left px-4 py-3 hover:bg-accent transition-colors border-b border-border last:border-0"
                          onClick={() => {
                            setShowPipelineMenu(false);
                            navigate('/project/batch/new');
                          }}
                        >
                          <div className="font-medium text-sm">16S rRNA (EPI2ME)</div>
                          <div className="text-xs text-muted-foreground">Bacterial community profiling</div>
                        </button>
                        <button
                          className="w-full text-left px-4 py-3 hover:bg-accent transition-colors"
                          onClick={() => {
                            setShowPipelineMenu(false);
                            navigate('/project/coi/new');
                          }}
                        >
                          <div className="font-medium text-sm">COI / Eukaryota (QIIME 2)</div>
                          <div className="text-xs text-muted-foreground">Eukaryote eDNA profiling</div>
                        </button>
                      </div>
                    </>
                  )}
                </div>

                <Button variant="ghost" onClick={() => navigate('/dashboard')}>
                  Dashboard
                </Button>
                <Button variant="ghost" onClick={() => navigate('/metadata')}>
                  Metadata
                </Button>
                {/* Admin link is only shown to users with role=admin. */}
                {isAdmin() && (
                  <Button variant="ghost" onClick={() => navigate('/admin')}>
                    Admin
                  </Button>
                )}


                <Button variant="outline" onClick={handleLogout}>
                  Logout
                </Button>
              </>
            ) : (
              <>
                {/* Unauthenticated nav: Login, Sign Up */}
                <Button variant="ghost" asChild>
                  <Link to="/login">Login</Link>
                </Button>
                <Button asChild>
                  <Link to="/signup">Sign Up</Link>
                </Button>
              </>
            )}
            {/* Theme toggle is always visible. */}
            <ThemeToggle />
          </div>
        </div>
      </div>
    </nav>
  );
}
